import React, { useCallback, useMemo, useState } from 'react'
import { IconButton, Tooltip } from '@material-ui/core'
import HierarchicalOpalGrid from '../../../DenaliDataPipeline/report/HierarchicalOpalGrid'
import GridColumnConfigModal from '../../../QueryBlueprint/GridColumnConfigModal'
import useStyleVariant from '../../../QueryBlueprint/hooks/useStyleVariant'
import SydButton from '../../../../commonDesign/Button'
import { ICON_NAMES } from '../../../../../constants'
import Icon from '../../../../atoms/Icon'
import { useFormattingContext } from '../../../FormattingProvider/FormattingContext'
import { useSyncedTransactions } from './SyncedTransactionsProvider'
import { PAGE_SIZE_OPTIONS } from './constants'
import { buildTanstackColumns } from './columnDefs'
import SyncedTransactionsTextSearch from './SyncedTransactionsTextSearch'
import SyncedTransactionsExport from './SyncedTransactionsExport'
import SyncedTransactionsFilterModal, { countActiveFilters } from './SyncedTransactionsFilterModal'

const actionsRow = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 0 4px 0'
}

const actionsLeft = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px'
}

const actionsRight = {
  display: 'flex',
  alignItems: 'center',
  gap: '2px'
}

const filterBadge = {
  minWidth: '18px',
  height: '18px',
  borderRadius: '9px',
  background: 'var(--color-primary-500)',
  color: '#fff',
  fontSize: '11px',
  fontWeight: 700,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 1,
  padding: '0 5px',
  marginLeft: '4px'
}

export default function SyncedTransactionsGrid () {
  const {
    config,
    columns,
    mergedColumns,
    handleColumnConfigSave,
    rows,
    total,
    isFetching,
    sortState,
    pageState,
    filterState,
    onFilterChange,
    onSortChange,
    onPageChange
  } = useSyncedTransactions()

  const { formatter } = useFormattingContext()

  const tableVariantConfig = useMemo(() => ({
    styleVariant: config.tableStyleVariant ?? 'default',
    styleVariantParams: config.tableStyleVariantParams ?? {}
  }), [config.tableStyleVariant, config.tableStyleVariantParams])
  const variant = useStyleVariant('Hierarchical Grid', tableVariantConfig)

  const defaultColumns = useMemo(() => columns, [columns])

  const tanstackColumns = useMemo(
    () => buildTanstackColumns(mergedColumns, formatter),
    [mergedColumns, formatter]
  )

  const [configOpen, setConfigOpen] = useState(false)
  const handleOpenConfig = useCallback(() => setConfigOpen(true), [])
  const handleCloseConfig = useCallback(() => setConfigOpen(false), [])

  const [filtersOpen, setFiltersOpen] = useState(false)
  const handleOpenFilters = useCallback(() => setFiltersOpen(true), [])
  const handleCloseFilters = useCallback(() => setFiltersOpen(false), [])
  const handleApplyFilters = useCallback((newState) => {
    onFilterChange(newState, true)
  }, [onFilterChange])
  const activeFilterCount = useMemo(() => countActiveFilters(filterState), [filterState])

  return (
    <div>
      <div style={actionsRow}>
        <div style={actionsLeft}>
          <SyncedTransactionsTextSearch />
          <SydButton
            variant='ghost'
            size='sm'
            icon={ICON_NAMES.filters}
            onClick={handleOpenFilters}
          >
            Filters{activeFilterCount > 0 ? <span style={filterBadge}>{activeFilterCount}</span> : null}
          </SydButton>
        </div>
        <div style={actionsRight}>
          {config.showExport !== false ? <SyncedTransactionsExport /> : null}
          {config.showColumnConfig !== false ? (
            <Tooltip title='Customize Columns'>
              <IconButton onClick={handleOpenConfig}>
                <Icon name={ICON_NAMES.viewListConfig} customSize='22px' />
              </IconButton>
            </Tooltip>
          ) : null}
        </div>
      </div>

      <HierarchicalOpalGrid
        data={rows}
        columns={tanstackColumns}
        isFetching={isFetching}
        pageState={pageState}
        sortState={sortState}
        onPageChange={onPageChange}
        onSortChange={onSortChange}
        manualPagination
        manualSorting
        totalRows={total}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        className={variant.tableClassName}
        style={variant.styles}
        {...variant.props}
      />

      <GridColumnConfigModal
        open={configOpen}
        onClose={handleCloseConfig}
        columns={mergedColumns}
        defaultColumns={defaultColumns}
        onSave={handleColumnConfigSave}
      />

      <SyncedTransactionsFilterModal
        open={filtersOpen}
        onClose={handleCloseFilters}
        filterState={filterState}
        availableFilters={config.availableFilters}
        onApply={handleApplyFilters}
      />
    </div>
  )
}
